import { useState, useRef } from 'react';
import { Plus, Upload } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { PHOTO_TAGS, EXPENSE_CATEGORIES, PAYMENT_TYPES, INPUT } from './config';
import { MoneyInput } from '../../components/common/MoneyInput';
import { DatePicker } from '../../components/common/DatePicker';
import { formatMoney } from '../../lib/money';
import { PAYMENT_METHODS } from '../../data/enums';
import type { ProjectPhoto, ProjectExpense, ProjectPayment, ProjectAudit } from '../../types';

// ── Photos Section ──
interface PhotoProps {
  grouped: Record<string, ProjectPhoto[]>;
  onUpload?: (file: File, tag: 'before' | 'during' | 'after') => Promise<boolean>;
}
export function PhotosSection({ grouped, onUpload }: PhotoProps) {
  const [uploading, setUploading] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFileChange = async (tag: 'before' | 'during' | 'after', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    setUploading(tag);
    await onUpload(file, tag);
    setUploading(null);
    if (inputRefs.current[tag]) inputRefs.current[tag]!.value = '';
  };

  return (
    <div>
      {(['before', 'during', 'after'] as const).map(tag => {
        const photos = grouped[tag] ?? [];
        const pt = PHOTO_TAGS[tag];
        return (
          <div key={tag} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: pt.bg, color: pt.fg }}>{pt.label}</span>
              <span style={{ color: palette.g400 }}>{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
              {onUpload && (
                <>
                  <button
                    onClick={() => inputRefs.current[tag]?.click()}
                    disabled={uploading === tag}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px',
                      borderRadius: 4, border: `1px solid ${palette.g200}`, background: palette.white,
                      fontSize: 10, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit',
                      marginLeft: 'auto',
                    }}
                  >
                    <Upload size={10} /> {uploading === tag ? 'Uploading...' : 'Upload'}
                  </button>
                  <input
                    ref={el => { inputRefs.current[tag] = el; }}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={e => handleFileChange(tag, e)}
                  />
                </>
              )}
            </div>
            {photos.length === 0 ? (
              <div style={{ fontSize: 12, color: palette.g400, padding: '4px 0' }}>No {tag} photos.</div>
            ) : (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {photos.map(p => (
                  <div key={p.id} style={{ width: 64, height: 64, borderRadius: 6, background: palette.g100, overflow: 'hidden', position: 'relative' }}>
                    <img src={p.storage_path} alt={p.caption ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    {p.caption && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: palette.white, fontSize: 8, padding: '2px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.caption}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Expenses Section ──
interface ExpenseProps { expenses: ProjectExpense[]; total: number; budget: number | null; onLog: (data: Partial<ProjectExpense>) => Promise<boolean>; }
export function ExpensesSection({ expenses, total, budget, onLog }: ExpenseProps) {
  const [show, setShow] = useState(false);
  const [cat, setCat] = useState('materials');
  const [desc, setDesc] = useState('');
  const [amt, setAmt] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const handleAdd = async () => {
    if (!desc.trim() || !amt) return;
    // Q1.5 boundary: onLog accepts number; amt is canonical after MoneyInput blur.
    const ok = await onLog({ category: cat, description: desc.trim(), amount: Number(amt), expense_date: date });
    if (ok) { setDesc(''); setAmt(''); setShow(false); }
  };

  const pct = budget ? Math.min(100, Math.round(total / budget * 100)) : 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: palette.g700 }}>
          ${total.toLocaleString('en-CA', { minimumFractionDigits: 0 })}
          {budget ? ` of $${budget.toLocaleString('en-CA')} (${pct}%)` : ' total'}
        </span>
        <button onClick={() => setShow(!show)} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 10px', borderRadius: 4, border: 'none', background: palette.accent, color: palette.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={12} /> Log Expense</button>
      </div>
      {budget && (
        <div style={{ height: 4, borderRadius: 2, background: palette.g200, marginBottom: 10 }}>
          <div style={{ height: 4, borderRadius: 2, background: pct > 90 ? palette.red : palette.accent, width: `${pct}%`, transition: 'width 0.3s' }} />
        </div>
      )}

      {show && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <select style={{ ...INPUT, width: 100 }} value={cat} onChange={e => setCat(e.target.value)}>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input style={{ ...INPUT, flex: 1, minWidth: 100 }} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description" />
          <MoneyInput value={amt} onChange={setAmt} placeholder="Amount" style={{ width: 110 }} />
          <div style={{ width: 140 }}>
            <DatePicker value={date || null} onChange={(v) => setDate(v ?? new Date().toISOString().split('T')[0])} maxDate={new Date().toISOString().split('T')[0]} clearable={false} />
          </div>
          <button onClick={handleAdd} style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: palette.green, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Add</button>
        </div>
      )}

      {expenses.length === 0 ? (
        <div style={{ fontSize: 12, color: palette.g400 }}>No expenses logged.</div>
      ) : (
        expenses.slice(0, 10).map(ex => (
          <div key={ex.id} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${palette.g100}`, fontSize: 12 }}>
            <span style={{ color: palette.g500, width: 70 }}>{ex.expense_date}</span>
            <span style={{ padding: '0 4px', borderRadius: 2, background: palette.g100, color: palette.g500, fontSize: 10, textTransform: 'capitalize' }}>{ex.category}</span>
            <span style={{ flex: 1, color: palette.g700 }}>{ex.description}</span>
            <span style={{ fontWeight: 600, color: palette.g800 }}>{formatMoney(ex.amount)}</span>
          </div>
        ))
      )}
    </div>
  );
}

// ── Payments Section ──
interface PaymentProps { payments: ProjectPayment[]; totalPaid: number; onRecord: (type: string, amount: number, method: string | null) => Promise<boolean>; }
export function PaymentsSection({ payments, totalPaid, onRecord }: PaymentProps) {
  const [show, setShow] = useState(false);
  const [type, setType] = useState('deposit');
  const [amt, setAmt] = useState('');
  const [method, setMethod] = useState('');

  const handleAdd = async () => {
    if (!amt) return;
    // Q1.5 boundary: onRecord accepts number; amt is canonical after MoneyInput blur.
    const ok = await onRecord(type, Number(amt), method || null);
    if (ok) { setAmt(''); setMethod(''); setShow(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: palette.g700 }}>Total paid: {formatMoney(totalPaid)}</span>
        <button onClick={() => setShow(!show)} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 10px', borderRadius: 4, border: 'none', background: palette.accent, color: palette.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={12} /> Record Payment</button>
      </div>
      {show && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <select style={{ ...INPUT, width: 100 }} value={type} onChange={e => setType(e.target.value)}>
            {Object.entries(PAYMENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <MoneyInput value={amt} onChange={setAmt} placeholder="Amount" style={{ width: 110 }} />
          <select style={{ ...INPUT, width: 120 }} value={method} onChange={e => setMethod(e.target.value)}>
            <option value="">Method</option>
            {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <button onClick={handleAdd} style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: palette.green, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Record</button>
        </div>
      )}
      {payments.length === 0 ? (
        <div style={{ fontSize: 12, color: palette.g400 }}>No payments recorded.</div>
      ) : (
        payments.map(p => (
          <div key={p.id} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${palette.g100}`, fontSize: 12 }}>
            <span style={{ padding: '0 6px', borderRadius: 3, background: palette.greenWash, color: palette.green, fontSize: 10, fontWeight: 600, textTransform: 'capitalize' }}>{p.type}</span>
            <span style={{ flex: 1, color: palette.g500 }}>{p.paid_at?.split('T')[0] ?? '—'} {p.payment_method ? `• ${p.payment_method}` : ''}</span>
            <span style={{ fontWeight: 600, color: palette.g800 }}>{formatMoney(p.amount)}</span>
          </div>
        ))
      )}
    </div>
  );
}

// ── Trust Badge Section ──
interface TrustProps { gpsVerified: boolean; hasBefore: boolean; hasAfter: boolean; clientConfirmed: boolean; isPaid: boolean; }
export function TrustBadgeSection({ gpsVerified, hasBefore, hasAfter, clientConfirmed, isPaid }: TrustProps) {
  const items = [
    { label: 'GPS verified', ok: gpsVerified },
    { label: 'Before photos', ok: hasBefore },
    { label: 'After photos', ok: hasAfter },
    { label: 'Client confirmed', ok: clientConfirmed },
    { label: 'Invoice paid', ok: isPaid },
  ];
  const allDone = items.every(i => i.ok);

  return (
    <div>
      {allDone && <div style={{ fontSize: 12, fontWeight: 600, color: palette.green, marginBottom: 8 }}>Trust Badge Earned</div>}
      {items.map(i => (
        <div key={i.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
          <span style={{ color: i.ok ? palette.green : palette.g300, fontSize: 14 }}>{i.ok ? '\u2713' : '\u2717'}</span>
          <span style={{ color: i.ok ? palette.g700 : palette.g400 }}>{i.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Audit Trail Section ──
interface AuditProps { entries: ProjectAudit[]; }
export function AuditTrailSection({ entries }: AuditProps) {
  if (entries.length === 0) return <div style={{ fontSize: 12, color: palette.g400 }}>No activity yet.</div>;
  return (
    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
      {entries.map(e => (
        <div key={e.id} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: `1px solid ${palette.g100}`, fontSize: 12 }}>
          <span style={{ color: palette.g400, width: 80, flexShrink: 0 }}>{e.performed_at.split('T')[0]}</span>
          <span style={{ fontWeight: 500, color: palette.g700 }}>{e.action.replace(/_/g, ' ')}</span>
          {e.new_value && <span style={{ color: palette.g500 }}>&rarr; {e.new_value}</span>}
        </div>
      ))}
    </div>
  );
}
